import { motion } from "framer-motion";
import { useState, useEffect, useMemo, useCallback } from "react";
import * as WaterIcons from "../components/WaterIcons";
import {
  Gauge,
  Building2,
  Droplets,
  CircleDot,
  Activity,
  Waves,
  Unplug,
  Zap,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  ChevronDown,
  Users,
  Hash,
  CheckCircle,
  XCircle,
  X,
  Droplet,
  Battery,
  Wifi,
  Clock,
  AlertTriangle,
  MapPin,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  MoreVertical,
  Grid,
  List,
  Loader2,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Dot,
  Legend,
} from "recharts";
import { get, post } from "../Libraries/Ajax/HttpService";
import { Modal, Spin, Checkbox } from "antd";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  Position,
} from "reactflow";
import dagre from "dagre";
import "reactflow/dist/style.css";

import Meter from "../assets/images/Ico_5.png";
import Tank from "../assets/images/Ico_6.png";
import GPWell from "../assets/images/Ico_7.png";
import WaterPlants from "../assets/images/Ico_3.png";
import Pump from "../assets/images/Ico_1.png";
import Reservoir from "../assets/images/Ico_2.png";
import WaterMeter from "../assets/images/Ico_8.png";
import { useDistrict } from "../utils/DistrictContext";
import { useTheme } from "../context/ThemeContext";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ConfigProvider, DatePicker, Progress, theme as antTheme } from "antd";
import {
  schematicJSON_Original,
  schematicJSON_District2,
  fixedPositions_Original,
  fixedPositions_District2,
} from "../utils/schematicData";

// ====================================================================
// 1. DATA AND ASSETS
// ====================================================================
const { RangePicker } = DatePicker;
const images = {
  meter: Meter,
  storage: Tank,
  source: GPWell,
  plant: WaterPlants,
  pump: Pump,
  reservoir: Reservoir,
  supply: WaterMeter,
  consumer: WaterMeter,
  process: WaterMeter,
};
const iconMapping = {
  meter: WaterIcons.WaterMeterIcon,
  consumer: WaterIcons.PotableSupplyMeterIcon,
  supply: WaterIcons.PotableSupplyMeterIcon,
  process: WaterIcons.PotableSupplyMeterIcon,
  storage: WaterIcons.WaterTankIcon,
  reservoir: WaterIcons.StorageTankIcon,
  plant: WaterIcons.WaterPlantIcon,
  source: WaterIcons.WaterWellIcon,
  pump: WaterIcons.DistributionMeterIcon,
  distribution: WaterIcons.DistributionMeterIcon, // Added mapping
};
function InfoTile({ label, value, icon, variant = "default" }) {
  const { colors } = useTheme();
  const variantStyles = {
    default: {
      borderColor: `${colors.border}`,
      backgroundColor: `${colors.cardBg}0D`,
    },
    warning: { borderColor: `#eab30850`, backgroundColor: `#eab30810` },
    danger: { borderColor: `#ef444450`, backgroundColor: `#ef444410` },
  };

  return (
    <div
      className="rounded-xl border p-4 flex items-start gap-3"
      style={variantStyles[variant]}
    >
      <div
        className="mt-1"
        style={{
          color:
            variant === "danger"
              ? "#ef4444"
              : variant === "warning"
                ? "#eab308"
                : colors.primary,
        }}
      >
        {icon}
      </div>
      <div>
        <span
          className="text-xs font-semibold block mb-1"
          style={{ color: colors.textSecondary }}
        >
          {label}
        </span>
        <p className="text-sm font-bold" style={{ color: colors.text }}>
          {value || "N/A"}
        </p>
      </div>
    </div>
  );
}
const getLayoutedElements = (nodes, edges, direction = "TB", districtId) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: direction, nodesep: 300, ranksep: 380 });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((n) => g.setNode(n.id, { width: 100, height: 80 }));
  edges.forEach((e) => g.setEdge(e.from, e.to));
  dagre.layout(g);

  const fixedPositions =
    districtId === "FB151" ? fixedPositions_District2 : fixedPositions_Original;

  // List of all node IDs and structure lines that belong to the Orange (Non-Potable) side
  const nonPotableKeywords = [
    "non_potable",
    "206680",
    "195026",
    // "129122",
    "114476",
    "114477",
    "commonline5",
    "commonline8",
    "commonline9",
  ];

  const layoutedNodes = nodes.map((n) => {
    const pos = fixedPositions[n.id] || g.node(n.id);

    // Determine if node belongs to orange (non-potable) path
    const isOrangeNode = nonPotableKeywords.some((kw) => n.id.includes(kw));

    return {
      id: n.id,
      position: { x: pos.x, y: pos.y },
      data: { ...n, isOrange: isOrangeNode },
      type: "customNode",
    };
  });

  const layoutedEdges = edges.map((e, i) => {
    // If either the source or the target is in the non-potable list, color the pipe orange
    const isOrangePath = nonPotableKeywords.some(
      (kw) => e.from.includes(kw) || e.to.includes(kw),
    );

    const edgeColor = isOrangePath ? "#A855F7" : "#00e5ff";
    const cssClass = isOrangePath ? "glowing-edge-orange" : "glowing-edge-cyan";
    const isTargetEdge =
      e.from === "meter_206680" && e.to === "non_potable_reservoir";
    // const isUpwardEdge = e.from === "commonline10" && e.to === "commonline7";
    const isReverseArrow = e.from === "commonline7" && e.to === "commonline10";

    return {
      id: `e${i}`,
      source: e.from,
      target: e.to,
      type: "smoothstep",
      animated: false,
      className: cssClass,

      sourceHandle: isTargetEdge ? "right-source" : null,
      targetHandle: isTargetEdge ? "left-target" : null,

      markerEnd: isReverseArrow
        ? undefined
        : {
            type: "arrowclosed",
            color: edgeColor,
          },

      markerStart: isReverseArrow
        ? {
            type: "arrowclosed",
            color: edgeColor,
          }
        : undefined,

      style: {
        stroke: edgeColor,
      },
    };
  });

  return { layoutedNodes, layoutedEdges };
};

const SCADAGauge = ({ color, value }) => (
  <div className="relative flex flex-col items-center justify-center w-[180px] h-[180px]">
    <svg
      width="180"
      height="180"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="transform transition-transform group-hover:scale-110"
    >
      <defs>
        <filter id="gaugeGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
        <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Outer Casing */}
      <circle
        cx="40"
        cy="36"
        r="24"
        fill="url(#gaugeGrad)"
        stroke={color}
        strokeWidth="1.5"
        strokeOpacity="0.3"
      />
      <circle
        cx="40"
        cy="36"
        r="24"
        fill="none"
        stroke={color}
        strokeWidth="1"
        strokeDasharray="4 4"
        opacity="0.5"
        className="animate-[spin_20s_linear_infinite]"
        style={{ transformOrigin: "40px 36px" }}
      />

      {/* Inner Bezel */}
      <circle
        cx="40"
        cy="36"
        r="18"
        fill="none"
        stroke={color}
        strokeWidth="2"
        filter="url(#gaugeGlow)"
      />
      <circle cx="40" cy="36" r="18" fill="#0a1929" opacity="0.5" />

      {/* Ticks */}
      <g stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.8">
        <line x1="28" y1="36" x2="24" y2="36" />
        <line x1="56" y1="36" x2="52" y2="36" />
        <line x1="40" y1="24" x2="40" y2="20" />
        <line x1="31.5" y1="27.5" x2="28.5" y2="24.5" />
        <line x1="48.5" y1="27.5" x2="51.5" y2="24.5" />
      </g>

      {/* Dynamic Flow Arc Indicator */}
      <circle
        cx="40"
        cy="36"
        r="13"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeDasharray="81.6"
        strokeDashoffset="81.6"
        strokeLinecap="round"
        filter="url(#gaugeGlow)"
        opacity="0.6"
        transform="rotate(135 40 36)"
      >
        <animate
          attributeName="stroke-dashoffset"
          values="81.6; 40; 60; 20; 50; 81.6"
          dur="7s"
          repeatCount="indefinite"
          calcMode="spline"
          keyTimes="0; 0.15; 0.35; 0.6; 0.8; 1"
          keySplines="0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1"
        />
      </circle>

      {/* Animated Needle */}
      <path
        d="M40 36 L40 21"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        filter="url(#gaugeGlow)"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          values="-50 40 36; 45 40 36; -10 40 36; 60 40 36; 15 40 36; -50 40 36"
          dur="7s"
          repeatCount="indefinite"
          calcMode="spline"
          keyTimes="0; 0.15; 0.35; 0.6; 0.8; 1"
          keySplines="0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1"
        />
      </path>

      {/* Center Pivot */}
      <circle cx="40" cy="36" r="4" fill={color} filter="url(#gaugeGlow)" />
      <circle cx="40" cy="36" r="1.5" fill="#fff" />

      {/* Base/Stand */}
      <path
        d="M36 60 L44 60 L46 68 L34 68 Z"
        fill={color}
        fillOpacity="0.2"
        stroke={color}
        strokeWidth="1.5"
      />
      <rect
        x="30"
        y="68"
        width="20"
        height="4"
        rx="2"
        fill={color}
        filter="url(#gaugeGlow)"
      />
    </svg>
    <div className="absolute bottom-[16px] flex flex-col items-center bg-[#020e18] px-2 py-0.5 rounded border border-gray-700">
      <span className="font-mono text-[12px] font-bold text-white">
        {value}
      </span>
    </div>
  </div>
);

// 2. Tank (WITH Ladder inside)
const SCADATank = ({ color = "#10b981", level = 70 }) => {
  const liquidLevel = level;

  return (
    <div className="relative flex flex-col items-center justify-center w-[180px] h-[200px]">
      <svg
        width="180"
        height="180"
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="transform transition-transform group-hover:scale-110"
      >
        <defs>
          <filter id="tankGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <linearGradient id="cylinderGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0a1929" stopOpacity="0.9" />
            <stop offset="20%" stopColor={color} stopOpacity="0.15" />
            <stop offset="50%" stopColor={color} stopOpacity="0.3" />
            <stop offset="80%" stopColor={color} stopOpacity="0.15" />
            <stop offset="100%" stopColor="#0a1929" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="liquidGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="50%" stopColor={color} stopOpacity="0.7" />
            <stop offset="100%" stopColor={color} stopOpacity="0.3" />
          </linearGradient>
          <clipPath id="tankClip">
            <rect x="22" y="20" width="36" height="46" rx="2" />
          </clipPath>
        </defs>

        {/* Support Platform Base */}
        <rect
          x="18"
          y="70"
          width="44"
          height="4"
          fill="#0a1929"
          stroke={color}
          strokeWidth="1.5"
          strokeOpacity="0.6"
        />
        <line x1="20" y1="74" x2="20" y2="78" stroke={color} strokeWidth="2" />
        <line x1="60" y1="74" x2="60" y2="78" stroke={color} strokeWidth="2" />
        <line x1="40" y1="74" x2="40" y2="78" stroke={color} strokeWidth="2" />

        {/* Main Cylindrical Tank Body */}
        <rect
          x="22"
          y="20"
          width="36"
          height="46"
          rx="2"
          fill="url(#cylinderGrad)"
          stroke={color}
          strokeWidth="2"
          strokeOpacity="0.6"
          filter="url(#tankGlow)"
        />

        {/* Vertical Structural Lines */}
        <line
          x1="28"
          y1="20"
          x2="28"
          y2="66"
          stroke={color}
          strokeWidth="0.8"
          strokeOpacity="0.3"
        />
        <line
          x1="40"
          y1="20"
          x2="40"
          y2="66"
          stroke={color}
          strokeWidth="0.8"
          strokeOpacity="0.4"
        />
        <line
          x1="52"
          y1="20"
          x2="52"
          y2="66"
          stroke={color}
          strokeWidth="0.8"
          strokeOpacity="0.3"
        />

        {/* Horizontal Ring Stiffeners */}
        <line
          x1="22"
          y1="30"
          x2="58"
          y2="30"
          stroke={color}
          strokeWidth="1.5"
          strokeOpacity="0.5"
        />
        <line
          x1="22"
          y1="42"
          x2="58"
          y2="42"
          stroke={color}
          strokeWidth="1.5"
          strokeOpacity="0.5"
        />
        <line
          x1="22"
          y1="54"
          x2="58"
          y2="54"
          stroke={color}
          strokeWidth="1.5"
          strokeOpacity="0.5"
        />

        {/* Liquid Fill with Animation */}
        <g clipPath="url(#tankClip)">
          <rect
            x="22"
            y={66 - liquidLevel * 0.575}
            width="36"
            height={liquidLevel * 0.575}
            fill="url(#liquidGrad)"
            opacity="0.8"
          />
          {/* Wave animation on liquid surface */}
          <path
            d={`M22 ${66 - liquidLevel * 0.575} Q28 ${66 - liquidLevel * 0.575 - 2} 34 ${66 - liquidLevel * 0.575} T46 ${66 - liquidLevel * 0.575} T58 ${66 - liquidLevel * 0.575}`}
            stroke={color}
            strokeWidth="1.5"
            fill="none"
            opacity="0.8"
          >
            <animate
              attributeName="d"
              values={`M22 ${66 - liquidLevel * 0.575} Q28 ${66 - liquidLevel * 0.575 - 2} 34 ${66 - liquidLevel * 0.575} T46 ${66 - liquidLevel * 0.575} T58 ${66 - liquidLevel * 0.575};
                     M22 ${66 - liquidLevel * 0.575} Q28 ${66 - liquidLevel * 0.575 + 2} 34 ${66 - liquidLevel * 0.575} T46 ${66 - liquidLevel * 0.575} T58 ${66 - liquidLevel * 0.575};
                     M22 ${66 - liquidLevel * 0.575} Q28 ${66 - liquidLevel * 0.575 - 2} 34 ${66 - liquidLevel * 0.575} T46 ${66 - liquidLevel * 0.575} T58 ${66 - liquidLevel * 0.575}`}
              dur="2s"
              repeatCount="indefinite"
            />
          </path>
        </g>

        {/* Top Dome */}
        <ellipse
          cx="40"
          cy="20"
          rx="18"
          ry="6"
          fill="#0a1929"
          stroke={color}
          strokeWidth="2"
          strokeOpacity="0.6"
        />
        <ellipse
          cx="40"
          cy="19"
          rx="18"
          ry="5"
          fill="url(#cylinderGrad)"
          stroke={color}
          strokeWidth="1"
          strokeOpacity="0.4"
        />

        {/* Roof Manway */}
        <circle
          cx="40"
          cy="15"
          r="3"
          fill="#0a1929"
          stroke={color}
          strokeWidth="1.5"
        />
        <line x1="40" y1="15" x2="40" y2="10" stroke={color} strokeWidth="2" />
        <circle
          cx="40"
          cy="10"
          r="2"
          fill={color}
          filter="url(#tankGlow)"
          opacity="0.8"
        >
          <animate
            attributeName="opacity"
            values="0.8;0.4;0.8"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Bottom Outlet */}
        <rect
          x="38"
          y="66"
          width="4"
          height="4"
          fill="#0a1929"
          stroke={color}
          strokeWidth="1"
        />
        <line
          x1="40"
          y1="70"
          x2="40"
          y2="74"
          stroke={color}
          strokeWidth="2.5"
        />
        <path d="M40 74 L50 74" stroke={color} strokeWidth="2.5" />

        {/* Level Indicator Scale on Side */}
        <g opacity="0.6">
          <line
            x1="60"
            y1="30"
            x2="62"
            y2="30"
            stroke={color}
            strokeWidth="1"
          />
          <line
            x1="60"
            y1="42"
            x2="62"
            y2="42"
            stroke={color}
            strokeWidth="1"
          />
          <line
            x1="60"
            y1="54"
            x2="62"
            y2="54"
            stroke={color}
            strokeWidth="1"
          />
          <line
            x1="60"
            y1="66"
            x2="62"
            y2="66"
            stroke={color}
            strokeWidth="1"
          />
          <text x="64" y="32" fill={color} fontSize="4" fontFamily="monospace">
            100%
          </text>
          <text x="64" y="44" fill={color} fontSize="4" fontFamily="monospace">
            66%
          </text>
          <text x="64" y="56" fill={color} fontSize="4" fontFamily="monospace">
            33%
          </text>
          <text x="64" y="68" fill={color} fontSize="4" fontFamily="monospace">
            0%
          </text>
        </g>

        {/* Current Level Indicator */}
        <circle
          cx="61"
          cy={66 - liquidLevel * 0.575}
          r="2"
          fill={color}
          filter="url(#tankGlow)"
        >
          <animate
            attributeName="opacity"
            values="1;0.5;1"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Digital Display showing level */}
        <rect
          x="24"
          y="72"
          width="16"
          height="6"
          rx="1"
          fill="#0a1929"
          stroke={color}
          strokeWidth="0.8"
          opacity="0.8"
        />
        <text
          x="32"
          y="76.5"
          fill={color}
          fontSize="4"
          fontFamily="monospace"
          textAnchor="middle"
          opacity="0.9"
        >
          {/* {Math.round(liquidLevel)}% */}
        </text>
      </svg>
    </div>
  );
};

// 3. Reservoir (NO Ladder, liquid texture)
const SCADAReservoir = ({ color }) => (
  <div className="relative flex flex-col items-center justify-center w-[180px] h-[180px]">
    <svg
      width="190"
      height="180"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="transform transition-transform group-hover:scale-110"
    >
      <style>
        {`
          @keyframes dashFlowDown {
            to { stroke-dashoffset: -8; }
          }
        `}
      </style>
      <defs>
        <filter id="npResGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
        <linearGradient id="npWaterGrad" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0.8" />
          <stop offset="60%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
        <pattern
          id="npWave"
          x="0"
          y="0"
          width="12"
          height="12"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M0 6 Q3 0 6 6 T12 6"
            fill="none"
            stroke={color}
            strokeWidth="1"
            strokeOpacity="0.5"
          />
        </pattern>
        <pattern
          id="hazardStripes"
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="3" height="6" fill={color} fillOpacity="0.7" />
        </pattern>
      </defs>

      {/* Sloped Open Settling Basin Structure */}
      <path
        d="M10 28 L24 64 L56 64 L70 28"
        fill="#0a1929"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        filter="url(#npResGlow)"
      />

      {/* Structural Embankment Reinforcements */}
      <line
        x1="17"
        y1="46"
        x2="24"
        y2="64"
        stroke={color}
        strokeWidth="1.5"
        strokeOpacity="0.4"
      />
      <line
        x1="63"
        y1="46"
        x2="56"
        y2="64"
        stroke={color}
        strokeWidth="1.5"
        strokeOpacity="0.4"
      />
      <line
        x1="40"
        y1="46"
        x2="40"
        y2="64"
        stroke={color}
        strokeWidth="1.5"
        strokeOpacity="0.3"
      />

      {/* Reclaimed / Non-Potable Liquid Body */}
      <path
        d="M14 38 L24 64 L56 64 L66 38 Z"
        fill="url(#npWaterGrad)"
        filter="url(#npResGlow)"
      />
      <path d="M14 38 L24 64 L56 64 L66 38 Z" fill="url(#npWave)" />

      {/* Top Hazard Warning Edge / Barrier */}
      <path d="M10 28 L70 28" stroke="url(#hazardStripes)" strokeWidth="3" />
      <path
        d="M10 26 L70 26"
        stroke={color}
        strokeWidth="1"
        strokeOpacity="0.5"
      />

      {/* Discharge Influent Pipe */}
      <path
        d="M2 20 L16 20 L16 28"
        fill="none"
        stroke={color}
        strokeWidth="3"
        filter="url(#npResGlow)"
      />
      <rect
        x="14"
        y="16"
        width="4"
        height="8"
        rx="1"
        fill="#0a1929"
        stroke={color}
        strokeWidth="1.5"
      />

      {/* Animated Discharge Flow */}
      <line
        x1="16"
        y1="28"
        x2="16"
        y2="42"
        stroke={color}
        strokeWidth="2"
        strokeDasharray="4 4"
        filter="url(#npResGlow)"
        style={{ animation: "dashFlowDown 0.6s linear infinite" }}
      />

      {/* Surface Aerators / Mechanical Agitators */}
      <g transform="translate(32, 44)">
        <ellipse
          cx="0"
          cy="0"
          rx="6"
          ry="3"
          fill="#0a1929"
          stroke={color}
          strokeWidth="1.5"
        />
        <line
          x1="-3"
          y1="-3"
          x2="3"
          y2="3"
          stroke={color}
          strokeWidth="1.5"
          className="animate-[spin_3s_linear_infinite]"
          style={{ transformOrigin: "0 0" }}
        />
      </g>
      <g transform="translate(52, 52)">
        <ellipse
          cx="0"
          cy="0"
          rx="5"
          ry="2.5"
          fill="#0a1929"
          stroke={color}
          strokeWidth="1.5"
        />
        <line
          x1="-2.5"
          y1="-2.5"
          x2="2.5"
          y2="2.5"
          stroke={color}
          strokeWidth="1.5"
          className="animate-[spin_2.5s_linear_infinite]"
          style={{ transformOrigin: "0 0" }}
        />
      </g>

      {/* Status Blinker Light */}
      <rect
        x="64"
        y="20"
        width="4"
        height="4"
        rx="1"
        fill="#0a1929"
        stroke={color}
        strokeWidth="1"
      />
      <circle
        cx="66"
        cy="22"
        r="1.5"
        fill={color}
        filter="url(#npResGlow)"
        className="animate-pulse"
      />
    </svg>
  </div>
);

// 4. GP Well (Motor below, Drop above with Arrow)
const SCADAWell = ({ color = "#60a5fa", position = 70 }) => {
  const rotation = (position / 100) * 720;

  return (
    <div className="relative flex flex-col items-center justify-center w-[180px] h-[200px]">
      <svg
        width="180"
        height="180"
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="valveGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <linearGradient id="bodyGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0a1929" stopOpacity="0.9" />
            <stop offset="50%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor="#0a1929" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient
            id="handwheelGrad"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor={color} stopOpacity="0.5" />
            <stop offset="100%" stopColor="#0a1929" stopOpacity="0.8" />
          </linearGradient>
        </defs>

        {/* Inlet Pipeline */}
        <rect
          x="4"
          y="48"
          width="20"
          height="8"
          fill="#0a1929"
          stroke={color}
          strokeWidth="1.5"
          strokeOpacity="0.6"
        />
        <line
          x1="4"
          y1="52"
          x2="24"
          y2="52"
          stroke={color}
          strokeWidth="0.8"
          strokeOpacity="0.4"
        />

        {/* Outlet Pipeline */}
        <rect
          x="56"
          y="48"
          width="20"
          height="8"
          fill="#0a1929"
          stroke={color}
          strokeWidth="1.5"
          strokeOpacity="0.6"
        />
        <line
          x1="56"
          y1="52"
          x2="76"
          y2="52"
          stroke={color}
          strokeWidth="0.8"
          strokeOpacity="0.4"
        />

        {/* Gate Valve Body - Rectangular industrial style */}
        <rect
          x="24"
          y="40"
          width="32"
          height="24"
          rx="2"
          fill="url(#bodyGrad)"
          stroke={color}
          strokeWidth="2.5"
          filter="url(#valveGlow)"
        />

        {/* Body Bolts */}
        <circle cx="28" cy="44" r="1.5" fill={color} opacity="0.6" />
        <circle cx="52" cy="44" r="1.5" fill={color} opacity="0.6" />
        <circle cx="28" cy="60" r="1.5" fill={color} opacity="0.6" />
        <circle cx="52" cy="60" r="1.5" fill={color} opacity="0.6" />

        {/* Valve Bonnet (top extension) */}
        <rect
          x="34"
          y="24"
          width="12"
          height="16"
          rx="1"
          fill="#0a1929"
          stroke={color}
          strokeWidth="2"
          strokeOpacity="0.7"
        />

        {/* Stem Guide */}
        <rect x="38" y="16" width="4" height="8" fill={color} opacity="0.4" />

        {/* Rising Stem - shows valve position */}
        <rect
          x="38.5"
          y={16 + (position / 100) * 16}
          width="3"
          height={24 - (position / 100) * 16}
          fill={color}
          filter="url(#valveGlow)"
        >
          <animate
            attributeName="opacity"
            values="0.9;0.6;0.9"
            dur="2s"
            repeatCount="indefinite"
          />
        </rect>

        {/* Handwheel Assembly */}
        <circle
          cx="40"
          cy="12"
          r="10"
          fill="none"
          stroke={color}
          strokeWidth="2"
          filter="url(#valveGlow)"
        />
        <circle
          cx="40"
          cy="12"
          r="7"
          fill="url(#handwheelGrad)"
          stroke={color}
          strokeWidth="1.5"
        />

        {/* Handwheel Spokes - animated rotation */}
        <g transform={`rotate(${rotation} 40 12)`}>
          <line
            x1="40"
            y1="5"
            x2="40"
            y2="19"
            stroke={color}
            strokeWidth="1.5"
            strokeOpacity="0.8"
          />
          <line
            x1="33"
            y1="12"
            x2="47"
            y2="12"
            stroke={color}
            strokeWidth="1.5"
            strokeOpacity="0.8"
          />
          <line
            x1="35.5"
            y1="7.5"
            x2="44.5"
            y2="16.5"
            stroke={color}
            strokeWidth="1.2"
            strokeOpacity="0.6"
          />
          <line
            x1="44.5"
            y1="7.5"
            x2="35.5"
            y2="16.5"
            stroke={color}
            strokeWidth="1.2"
            strokeOpacity="0.6"
          />
          <animateTransform
            attributeName="transform"
            type="rotate"
            from={`${rotation} 40 12`}
            to={`${rotation + 360} 40 12`}
            dur="8s"
            repeatCount="indefinite"
          />
        </g>

        {/* Center Hub */}
        <circle
          cx="40"
          cy="12"
          r="2.5"
          fill="#0a1929"
          stroke={color}
          strokeWidth="1"
        />

        {/* Position Indicator Display */}
        <rect
          x="62"
          y="28"
          width="14"
          height="8"
          rx="1"
          fill="#0a1929"
          stroke={color}
          strokeWidth="1"
          opacity="0.9"
        />
        <text
          x="69"
          y="33.5"
          fill={color}
          fontSize="4.5"
          fontFamily="monospace"
          textAnchor="middle"
        >
          {/* {Math.round(position)}% */}
        </text>

        {/* Position Indicator Pointer */}
        <path d="M60 32 L62 32" stroke={color} strokeWidth="1.5" />
        <circle cx="59" cy="32" r="1.5" fill={color} filter="url(#valveGlow)">
          <animate
            attributeName="opacity"
            values="1;0.5;1"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Status Label */}
        <text
          x="40"
          y="75"
          fill={color}
          fontSize="5"
          fontFamily="monospace"
          textAnchor="middle"
          opacity="0.7"
        >
          {position > 90 ? "OPEN" : position < 10 ? "CLOSED" : ""}
        </text>

        {/* Flow Direction Arrow */}
        <g opacity="0.5">
          <path d="M14 46 L14 42 L18 44 Z" fill={color} />
          <path d="M66 46 L66 42 L70 44 Z" fill={color} />
        </g>

        {/* Gate Position Visualization inside body */}
        <rect
          x="28"
          y={44 + (position / 100) * 12}
          width="24"
          height="4"
          fill={color}
          opacity="0.3"
        />
      </svg>
    </div>
  );
};

// 5. Water Drops (Total Produced / Potable Supply / Demand)
const SCADADrops = ({ color }) => (
  <div className="relative flex flex-col items-center justify-center w-[180px] h-[180px]">
    <svg
      width="180"
      height="180"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="transform transition-transform group-hover:scale-110"
    >
      <defs>
        <filter id="pumpGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
        <linearGradient id="pumpVolute" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Piping Intake */}
      <rect
        x="12"
        y="34"
        width="16"
        height="12"
        rx="2"
        fill="none"
        stroke={color}
        strokeWidth="2"
        filter="url(#pumpGlow)"
      />

      {/* Piping Outtake (Vertical) */}
      <rect
        x="36"
        y="12"
        width="12"
        height="16"
        rx="2"
        fill="none"
        stroke={color}
        strokeWidth="2"
        filter="url(#pumpGlow)"
      />

      {/* Pump Housing / Volute Casing */}
      <circle
        cx="42"
        cy="40"
        r="18"
        fill="url(#pumpVolute)"
        stroke={color}
        strokeWidth="2"
        filter="url(#pumpGlow)"
      />

      {/* Impeller Housing & Glow */}
      <circle
        cx="42"
        cy="40"
        r="10"
        fill="#0a1929"
        stroke={color}
        strokeWidth="2"
      />

      {/* Rotating Impeller */}
      <g
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeOpacity="0.9"
        filter="url(#pumpGlow)"
        style={{ transformOrigin: "42px 40px" }}
        className="animate-[spin_4s_linear_infinite]"
      >
        <circle cx="42" cy="40" r="3" fill={color} />
        {/* Curved Blades */}
        <path d="M42 37 Q46 34 46 30" fill="none" />
        <path d="M42 43 Q38 46 38 50" fill="none" />
        <path d="M39 40 Q36 36 32 36" fill="none" />
        <path d="M45 40 Q48 44 52 44" fill="none" />
      </g>

      {/* Motor Indicator/Coupling */}
      <rect
        x="56"
        y="32"
        width="12"
        height="16"
        rx="2"
        fill="#0a1929"
        stroke={color}
        strokeWidth="1.5"
        opacity="0.8"
      />
      <line
        x1="60"
        y1="36"
        x2="64"
        y2="36"
        stroke={color}
        strokeWidth="1"
        strokeOpacity="0.5"
      />
      <line
        x1="60"
        y1="40"
        x2="64"
        y2="40"
        stroke={color}
        strokeWidth="1"
        strokeOpacity="0.5"
      />
      <line
        x1="60"
        y1="44"
        x2="64"
        y2="44"
        stroke={color}
        strokeWidth="1"
        strokeOpacity="0.5"
      />

      {/* Flange / Connections */}
      <line x1="16" y1="32" x2="16" y2="48" stroke={color} strokeWidth="2.5" />
      <line x1="34" y1="16" x2="50" y2="16" stroke={color} strokeWidth="2.5" />

      {/* Base Mounts */}
      <rect
        x="36"
        y="58"
        width="12"
        height="8"
        rx="1"
        fill={color}
        fillOpacity="0.2"
        stroke={color}
        strokeWidth="1.5"
      />
      <line
        x1="32"
        y1="66"
        x2="52"
        y2="66"
        stroke={color}
        strokeWidth="2"
        filter="url(#pumpGlow)"
      />
    </svg>
  </div>
);

// 6. Isometric Motor (Non-Potable Plant)
const SCADAIsoMotor = ({ color }) => (
  <div className="relative flex flex-col items-center justify-center w-[180px] h-[180px]">
    <svg
      width="180"
      height="180"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="transform transition-transform group-hover:scale-110"
    >
      <style>
        {`
          @keyframes dashFlow {
            to { stroke-dashoffset: -8; }
          }
        `}
      </style>
      <defs>
        <filter id="npPlantGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
        <pattern
          id="warningStripes"
          width="8"
          height="8"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="4" height="8" fill={color} fillOpacity="0.6" />
        </pattern>
        <linearGradient id="npTankBody" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0a1929" stopOpacity="0.9" />
          <stop offset="50%" stopColor={color} stopOpacity="0.15" />
          <stop offset="100%" stopColor="#0a1929" stopOpacity="0.9" />
        </linearGradient>
      </defs>

      {/* Industrial Foundation Base */}
      <rect
        x="8"
        y="60"
        width="64"
        height="6"
        rx="1"
        fill="#0a1929"
        stroke={color}
        strokeWidth="2"
        filter="url(#npPlantGlow)"
      />
      <line
        x1="12"
        y1="63"
        x2="68"
        y2="63"
        stroke={color}
        strokeWidth="1"
        strokeOpacity="0.5"
      />

      {/* Heavy Processing Tanks */}
      <path
        d="M16 60 L16 30 L32 20 L32 60 Z"
        fill="url(#npTankBody)"
        stroke={color}
        strokeWidth="2"
        filter="url(#npPlantGlow)"
      />

      {/* Animated liquid level in Tank 1 */}
      <path
        d="M16 60 L16 35 L32 25 L32 60 Z"
        fill={color}
        fillOpacity="0.1"
        stroke="none"
      >
        <animate
          attributeName="fill-opacity"
          values="0.1; 0.25; 0.1"
          dur="4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="d"
          values="M16 60 L16 35 L32 25 L32 60 Z; M16 60 L16 30 L32 20 L32 60 Z; M16 60 L16 35 L32 25 L32 60 Z"
          dur="4s"
          repeatCount="indefinite"
        />
      </path>

      <path
        d="M38 60 L38 36 L50 28 L50 60 Z"
        fill="url(#npTankBody)"
        stroke={color}
        strokeWidth="2"
        filter="url(#npPlantGlow)"
      />
      {/* Animated liquid level in Tank 2 */}
      <path
        d="M38 60 L38 40 L50 32 L50 60 Z"
        fill={color}
        fillOpacity="0.15"
        stroke="none"
      >
        <animate
          attributeName="fill-opacity"
          values="0.15; 0.3; 0.15"
          dur="3s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="d"
          values="M38 60 L38 40 L50 32 L50 60 Z; M38 60 L38 36 L50 28 L50 60 Z; M38 60 L38 40 L50 32 L50 60 Z"
          dur="3s"
          repeatCount="indefinite"
        />
      </path>

      <path
        d="M56 60 L56 42 L64 36 L64 60 Z"
        fill="url(#npTankBody)"
        stroke={color}
        strokeWidth="2"
        filter="url(#npPlantGlow)"
      />
      {/* Animated liquid level in Tank 3 */}
      <path
        d="M56 60 L56 46 L64 40 L64 60 Z"
        fill={color}
        fillOpacity="0.2"
        stroke="none"
      >
        <animate
          attributeName="fill-opacity"
          values="0.2; 0.35; 0.2"
          dur="5s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="d"
          values="M56 60 L56 46 L64 40 L64 60 Z; M56 60 L56 42 L64 36 L64 60 Z; M56 60 L56 46 L64 40 L64 60 Z"
          dur="5s"
          repeatCount="indefinite"
        />
      </path>

      {/* Warning Chevron Bands on Tanks */}
      <rect
        x="16"
        y="44"
        width="16"
        height="6"
        fill="url(#warningStripes)"
        stroke={color}
        strokeWidth="1"
      />
      <rect
        x="38"
        y="48"
        width="12"
        height="4"
        fill="url(#warningStripes)"
        stroke={color}
        strokeWidth="1"
      />
      <rect
        x="56"
        y="50"
        width="8"
        height="4"
        fill="url(#warningStripes)"
        stroke={color}
        strokeWidth="1"
      />

      {/* Connecting Flow Pipes */}
      <path
        d="M32 40 L38 40"
        stroke={color}
        strokeWidth="2.5"
        filter="url(#npPlantGlow)"
      />
      <path
        d="M50 50 L56 50"
        stroke={color}
        strokeWidth="2.5"
        filter="url(#npPlantGlow)"
      />

      {/* Chemical / Treatment Injection Unit */}
      <rect
        x="20"
        y="14"
        width="8"
        height="6"
        rx="1"
        fill="#0a1929"
        stroke={color}
        strokeWidth="1.5"
      />
      <circle
        cx="24"
        cy="17"
        r="1.5"
        fill={color}
        filter="url(#npPlantGlow)"
        className="animate-pulse"
      />

      {/* Vent / Fumes Indicator */}
      <path
        d="M44 28 L44 16"
        stroke={color}
        strokeWidth="1.5"
        strokeOpacity="0.7"
        strokeDasharray="4 4"
      >
        <animate
          attributeName="stroke-dashoffset"
          values="8; 0"
          dur="1s"
          repeatCount="indefinite"
        />
      </path>

      {/* Outflow Pipeline with Animated Flow */}
      <path
        d="M64 54 L76 54"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeDasharray="3 3"
        filter="url(#npPlantGlow)"
        style={{ animation: "dashFlow 0.5s linear infinite" }}
      />
    </svg>
  </div>
);

// 7. Water Plant (4 Modules Box) - Massive Scale
const SCADAPlant = ({ color }) => (
  <div
    className="relative flex items-center justify-center p-4 rounded-xl border border-opacity-40"
    style={{
      // borderColor: color,
      // background: "rgba(0, 20, 40, 0.5)",
      // boxShadow: `0 0 25px ${color}40 inset`,
      width: "480px",
      height: "180px",
    }}
  >
    <svg
      width="480"
      height="200"
      viewBox="0 0 200 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <filter id="plantGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
        <linearGradient id="waterGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.7" />
        </linearGradient>
        <linearGradient id="tankGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#0a1929" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0d1520" stopOpacity="1" />
        </linearGradient>
      </defs>

      {/* Main Building Base */}
      <rect
        x="60"
        y="40"
        width="80"
        height="45"
        rx="2"
        fill="#0a1929"
        stroke={color}
        strokeWidth="1.5"
        strokeOpacity="0.6"
      />
      <rect
        x="62"
        y="42"
        width="76"
        height="41"
        rx="1"
        fill="none"
        stroke={color}
        strokeWidth="0.6"
        strokeOpacity="0.3"
      />

      {/* Treatment Tanks (Left Side) */}
      <g>
        {/* Tank 1 */}
        <rect
          x="15"
          y="55"
          width="30"
          height="30"
          rx="3"
          fill="url(#tankGrad)"
          stroke={color}
          strokeWidth="1.2"
          strokeOpacity="0.7"
        />
        {/* Water Level */}
        <rect
          x="17"
          y="65"
          width="26"
          height="18"
          fill="url(#waterGrad)"
          opacity="0.8"
        >
          <animate
            attributeName="height"
            values="18;22;18"
            dur="3s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="y"
            values="65;61;65"
            dur="3s"
            repeatCount="indefinite"
          />
        </rect>
        {/* Bubbles Animation */}
        <circle cx="22" cy="75" r="1.5" fill={color} opacity="0.6">
          <animate
            attributeName="cy"
            values="75;60"
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.6;0"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="30" cy="78" r="1.2" fill={color} opacity="0.6">
          <animate
            attributeName="cy"
            values="78;62"
            dur="2.3s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.6;0"
            dur="2.3s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="38" cy="73" r="1" fill={color} opacity="0.6">
          <animate
            attributeName="cy"
            values="73;58"
            dur="1.8s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.6;0"
            dur="1.8s"
            repeatCount="indefinite"
          />
        </circle>
      </g>

      {/* Tank 2 */}
      <g>
        <rect
          x="155"
          y="55"
          width="30"
          height="30"
          rx="3"
          fill="url(#tankGrad)"
          stroke={color}
          strokeWidth="1.2"
          strokeOpacity="0.7"
        />
        {/* Water Level */}
        <rect
          x="157"
          y="60"
          width="26"
          height="23"
          fill="url(#waterGrad)"
          opacity="0.8"
        >
          <animate
            attributeName="height"
            values="23;20;23"
            dur="2.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="y"
            values="60;63;60"
            dur="2.5s"
            repeatCount="indefinite"
          />
        </rect>
        {/* Bubbles Animation */}
        <circle cx="162" cy="75" r="1.3" fill={color} opacity="0.6">
          <animate
            attributeName="cy"
            values="75;58"
            dur="2.2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.6;0"
            dur="2.2s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="170" cy="78" r="1.5" fill={color} opacity="0.6">
          <animate
            attributeName="cy"
            values="78;60"
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.6;0"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="178" cy="72" r="1.1" fill={color} opacity="0.6">
          <animate
            attributeName="cy"
            values="72;57"
            dur="1.9s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.6;0"
            dur="1.9s"
            repeatCount="indefinite"
          />
        </circle>
      </g>

      {/* Connecting Pipes */}
      {/* Pipe from Tank 1 to Building */}
      <line
        x1="45"
        y1="70"
        x2="60"
        y2="70"
        stroke={color}
        strokeWidth="2"
        strokeOpacity="0.7"
      />
      {/* Flow animation in pipe */}
      <circle cx="48" cy="70" r="1.5" fill={color} filter="url(#plantGlow)">
        <animate
          attributeName="cx"
          values="45;60;45"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Pipe from Building to Tank 2 */}
      <line
        x1="140"
        y1="70"
        x2="155"
        y2="70"
        stroke={color}
        strokeWidth="2"
        strokeOpacity="0.7"
      />
      {/* Flow animation in pipe */}
      <circle cx="145" cy="70" r="1.5" fill={color} filter="url(#plantGlow)">
        <animate
          attributeName="cx"
          values="140;155;140"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Building Windows/Sections */}
      {/* Top windows */}
      <rect
        x="70"
        y="45"
        width="12"
        height="10"
        rx="1"
        fill="#050a14"
        stroke={color}
        strokeWidth="0.5"
        strokeOpacity="0.5"
      />
      <rect
        x="88"
        y="45"
        width="12"
        height="10"
        rx="1"
        fill="#050a14"
        stroke={color}
        strokeWidth="0.5"
        strokeOpacity="0.5"
      />
      <rect
        x="106"
        y="45"
        width="12"
        height="10"
        rx="1"
        fill="#050a14"
        stroke={color}
        strokeWidth="0.5"
        strokeOpacity="0.5"
      />
      <rect
        x="124"
        y="45"
        width="12"
        height="10"
        rx="1"
        fill="#050a14"
        stroke={color}
        strokeWidth="0.5"
        strokeOpacity="0.5"
      />

      {/* Bottom control panels */}
      <rect
        x="70"
        y="63"
        width="12"
        height="15"
        rx="1"
        fill="#0d1520"
        stroke={color}
        strokeWidth="0.5"
        strokeOpacity="0.5"
      />
      <rect
        x="88"
        y="63"
        width="12"
        height="15"
        rx="1"
        fill="#0d1520"
        stroke={color}
        strokeWidth="0.5"
        strokeOpacity="0.5"
      />
      <rect
        x="106"
        y="63"
        width="12"
        height="15"
        rx="1"
        fill="#0d1520"
        stroke={color}
        strokeWidth="0.5"
        strokeOpacity="0.5"
      />
      <rect
        x="124"
        y="63"
        width="12"
        height="15"
        rx="1"
        fill="#0d1520"
        stroke={color}
        strokeWidth="0.5"
        strokeOpacity="0.5"
      />

      {/* Indicator LEDs on control panels */}
      <circle cx="76" cy="70" r="1.2" fill="#10b981" filter="url(#plantGlow)">
        <animate
          attributeName="opacity"
          values="1;0.3;1"
          dur="1.5s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="94" cy="70" r="1.2" fill="#eab308" filter="url(#plantGlow)">
        <animate
          attributeName="opacity"
          values="1;0.3;1"
          dur="1.8s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="112" cy="70" r="1.2" fill="#06b6d4" filter="url(#plantGlow)">
        <animate
          attributeName="opacity"
          values="1;0.3;1"
          dur="2.1s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="130" cy="70" r="1.2" fill="#10b981" filter="url(#plantGlow)">
        <animate
          attributeName="opacity"
          values="1;0.3;1"
          dur="1.6s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Roof Structure */}
      <polygon
        points="55,40 100,25 145,40"
        fill="#0d1520"
        stroke={color}
        strokeWidth="1"
        strokeOpacity="0.6"
      />
      <line
        x1="100"
        y1="25"
        x2="100"
        y2="15"
        stroke={color}
        strokeWidth="1.5"
        strokeOpacity="0.7"
      />
      {/* Chimney/Vent */}
      <rect
        x="97"
        y="10"
        width="6"
        height="5"
        fill="#0a1929"
        stroke={color}
        strokeWidth="0.8"
        strokeOpacity="0.6"
      />

      {/* Status Indicator Dots (Bottom of plant) */}
      <g>
        <circle cx="75" cy="92" r="1.5" fill="#10b981" filter="url(#plantGlow)">
          <animate
            attributeName="fill"
            values="#10b981;#84cc16;#eab308;#f97316;#ef4444;#f97316;#eab308;#84cc16;#10b981"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="85" cy="92" r="1.5" fill="#84cc16" filter="url(#plantGlow)">
          <animate
            attributeName="fill"
            values="#84cc16;#eab308;#f97316;#ef4444;#f97316;#eab308;#84cc16;#10b981;#84cc16"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx="95" cy="92" r="1.5" fill="#eab308" filter="url(#plantGlow)">
          <animate
            attributeName="fill"
            values="#eab308;#f97316;#ef4444;#f97316;#eab308;#84cc16;#10b981;#84cc16;#eab308"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="105"
          cy="92"
          r="1.5"
          fill="#f97316"
          filter="url(#plantGlow)"
        >
          <animate
            attributeName="fill"
            values="#f97316;#ef4444;#f97316;#eab308;#84cc16;#10b981;#84cc16;#eab308;#f97316"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="115"
          cy="92"
          r="1.5"
          fill="#ef4444"
          filter="url(#plantGlow)"
        >
          <animate
            attributeName="fill"
            values="#ef4444;#f97316;#eab308;#84cc16;#10b981;#84cc16;#eab308;#f97316;#ef4444"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="125"
          cy="92"
          r="1.5"
          fill="#10b981"
          filter="url(#plantGlow)"
        >
          <animate
            attributeName="fill"
            values="#10b981;#84cc16;#eab308;#f97316;#ef4444;#f97316;#eab308;#84cc16;#10b981"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
      </g>

      {/* Ground line */}
      <line
        x1="10"
        y1="85"
        x2="190"
        y2="85"
        stroke={color}
        strokeWidth="0.5"
        strokeOpacity="0.3"
        strokeDasharray="2,2"
      />
    </svg>
  </div>
);

// ==========================================
// THE CUSTOM NODE COMPONENT
// ==========================================
const CustomNode = ({ id, data }) => {
  const { colors, theme } = useTheme();
  const isNorthDistribution = id === "north_distribution_meter";
  const isSouthDistribution = id === "south_distribution_meter";
  if (data.type === "structure" || data.type === "label") {
    return (
      <div style={{ width: "1px", height: "1px", background: "transparent" }}>
        {data.type === "label" && (
          <div
            className="absolute font-bold text-3xl whitespace-nowrap -translate-x-1/2 -translate-y-1/2"
            style={{ color: colors.text }}
          >
            {data.label}
          </div>
        )}
        <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ opacity: 0 }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right-source"
          style={{ opacity: 0 }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="left-target"
          style={{ opacity: 0 }}
        />
      </div>
    );
  }

  // Auto-detect Orange Path based on IDs
  const isOrange =
    data.isOrange ||
    id.includes("non_potable") ||
    id.includes("129122") ||
    id.includes("195026") ||
    id.includes("206680") ||
    id.includes("114476") ||
    id.includes("114477");
  const themeColor = isOrange ? "#A855F7" : "#00e5ff";

  return (
    // We use flex-col so the labels sit perfectly below the icon without absolute positioning issues
    <div className="scada-node cursor-pointer">
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />

      {/* RENDER THE SPECIFIC MATCHING COMPONENT */}
      {id.includes("well") ? (
        <SCADAWell color={themeColor} />
      ) : id === "water_plant" ? (
        <SCADAPlant color={themeColor} />
      ) : id === "tank" || id === "dist2_tank" ? (
        <SCADATank color={themeColor} />
      ) : id === "non_potable_reservoir" ? (
        <SCADAReservoir color={themeColor} />
      ) : id === "total_produced" ||
        id.includes("consumer_potable") ||
        id.includes("consumer_non_potable") ||
        id.includes("supply") ||
        id === "total_demand" ? (
        <SCADADrops color={themeColor} />
      ) : id === "non_potable_plant" ? (
        <SCADAIsoMotor color={themeColor} />
      ) : (
        <SCADAGauge color={themeColor} value={data.value} />
      )}

      {/* LABELS BELOW THE ICON */}
      <div className="text-center mt-3 w-[180px]">
        <div
          className="text-[24px] font-bold tracking-wide leading-tight"
          style={{ textShadow: `0 0 6px ${themeColor}90`, color: colors.text }}
        >
          {data.label}
        </div>
        {(data.type === "meter" ||
          data.type === "consumer" ||
          data.type === "supply") &&
          !id.includes("plant") &&
          !id.includes("tank") && (
            <div
              className="text-[14px] font-semibold mt-1 leading-tight"
              style={{ color: themeColor }}
            >
              {/* 1. North Distribution Check */}
              {id === "north_distribution_meter" ? (
                <span className="uppercase tracking-wider">
                  North Distribution Meter
                </span>
              ) : /* 2. South Distribution Check */
              id === "south_distribution_meter" ? (
                <span className="uppercase tracking-wider">
                  South Distribution Meter
                </span>
              ) : /* 3. NEW: IC Check for 150850 and 150849 */
              id === "meter_150850" || id === "meter_150849" ? (
                <span
                  className="text-[20px] font-bold text-white tracking-widest"
                  style={{ color: colors.text }}
                >
                  IC
                </span>
              ) : (
                /* 4. Default Fallback for all other meters */
                <span className="text-gray-400 text-[12px] capitalize">
                  {/* {data.name?.replace(/_/g, " ")} */}
                </span>
              )}
            </div>
          )}
      </div>

      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};
export default function DigitalTwin({ onBack }) {
  const { colors, theme } = useTheme();
  const [activeTab, setActiveTab] = useState("digitalTwin");
  const [hoveredNode, setHoveredNode] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Modal States
  const [isSingleModalOpen, setIsSingleModalOpen] = useState(false);
  const [singleMeterData, setSingleMeterData] = useState(null);
  const [isAlertEnabled, setIsAlertEnabled] = useState(false);
  const [regNo, setRegNo] = useState("");
  const [multiModalNodeType, setMultiModalNodeType] = useState(null);
  const [isMultiModalOpen, setIsMultiModalOpen] = useState(false);
  const [multiMeterData, setMultiMeterData] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalStartDate, setModalStartDate] = useState(
    dayjs().subtract(30, "day").format("YYYY-MM-DD"),
  );
  const [modalEndDate, setModalEndDate] = useState(
    dayjs().format("YYYY-MM-DD"),
  );
  const [activeNode, setActiveNode] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Flow State
  const districtIdvalue = useDistrict();
  const districtId = districtIdvalue?.selectedDistrict;
  const isFB151 = districtId === "FB151";
  const navigate = useNavigate();

  const goToMeterLeakDetails = (meterData) => {
    // Generates a date 30 days ago, matching your other component's logic
    const thirtyDaysBack = dayjs().subtract(30, "day").format("YYYY-MM-DD");

    navigate(
      `/meter-leak-details?category=ALL&meter_id=${meterData.meter_id}&time_range=Current&selected_time=${thirtyDaysBack}`,
      {
        state: {
          water_loss_gallons: meterData.gallons || null,
          price: meterData.loss || null,
          leak_rate: meterData.leak_rate || null,
          fromTab: "digitalTwin",
        },
      },
    );
  };

  const currentSchematicJSON =
    districtId === "FB151" ? schematicJSON_District2 : schematicJSON_Original;
  const minZoomValue = useMemo(() => {
    return districtId === "FB151" ? 0.515 : 0.5;
  }, [districtId]);

  const fitViewOptionsValue = useMemo(() => {
    if (districtId === "FB151") {
      return { padding: 0.05, includeHiddenNodes: true };
    }
    return { padding: 0.2, includeHiddenNodes: true };
  }, [districtId]);

  const multiApiNodeNames = useMemo(() => {
    if (districtId === "FB151") {
      return ["Total_Supply", "Total_Demand", "Tank"];
    }
    return ["Produced", "NPS(P1)", "NPS(P2)"];
  }, [districtId]);

  const { layoutedNodes, layoutedEdges } = useMemo(
    () =>
      getLayoutedElements(
        currentSchematicJSON.nodes,
        currentSchematicJSON.edges,
        "TB",
        districtId,
      ),
    [districtId, currentSchematicJSON],
  );

  const nodeTypes = useMemo(() => ({ customNode: CustomNode }), []);

  // const handleSingleMeterClick = useCallback(
  //   async (node) => {
  //     setLoading(true);
  //     setIsSingleModalOpen(true);
  //     setError(null);
  //     setSingleMeterData(null);
  //     setActiveNode(node);

  //     let meter_id = node?.data?.name?.trim() || "";
  //     if (/^Well_\d+$/.test(meter_id)) {
  //       meter_id = meter_id.replace(/^Well_(\d+)$/, "Well #$1");
  //     }

  //     const user = JSON.parse(localStorage.getItem("MCData") || "{}");
  //     const thirtyDaysAgo = dayjs().subtract(30, "day").format("YYYY-MM-DD");
  //     const today = dayjs().format("YYYY-MM-DD");

  //     try {
  //       // 1. Fetch Anomaly API
  //       const response = await get("apis/anoamaly_detection", {
  //         params: {
  //           meter_id: meter_id,
  //           start_date: thirtyDaysAgo,
  //           end_date: today,
  //           time_range: "Custom",
  //         },
  //       });

  //       const payload = response.data;
  //       const rootObject = Array.isArray(payload) ? payload[0] : payload || {};

  //       // FIX: Extract hardware details from the array inside the API response
  //       const apiHardwareDetails =
  //         rootObject.meter_details && rootObject.meter_details.length > 0
  //           ? rootObject.meter_details[0]
  //           : {};

  //       // 2. Fetch Schematic details
  //       const detailsRes = await get(
  //         `apis/schematic_meter?meter_id=${encodeURIComponent(meter_id)}&user_id=${user?.userid}`,
  //       );

  //       setSingleMeterData({
  //         ...detailsRes.data.data, // Schematic API info
  //         ...apiHardwareDetails, // Anomaly API info (Address, SN, etc.)
  //         anomalyData: (rootObject.data || []).map((item) => ({
  //           name: item.day,
  //           gallons: item.total_daily_flow,
  //           threshold: item.threshold_value,
  //           rolling: item.upper_rolling_threshold,
  //           historical: item.historical_guardrail_upper,
  //           isAnomaly: item.anomaly === 1,
  //           anomalyReason: item.anomaly_reason,
  //           suppressedReason: item.anomaly_suppressed_reason,
  //         })),
  //         total_sum: rootObject.total_flow || 0,
  //       });

  //       setIsAlertEnabled(
  //         detailsRes.data.data?.meter_notification_active
  //           ?.notification_status || false,
  //       );
  //     } catch (e) {
  //       console.error("Error fetching anomaly data:", e);
  //       setError("Failed to load meter data.");
  //     } finally {
  //       setLoading(false);
  //     }
  //   },
  //   [goToMeterLeakDetails],
  // );
  // 1. Updated handler: just set the node and open modal
  const handleSingleMeterClick = useCallback((node) => {
    setActiveNode(node);
    setIsSingleModalOpen(true);
  }, []);

  // 2. New useEffect: Fetches data whenever node OR dates change
  useEffect(() => {
    // Only fetch if the modal is open and we have a node
    if (!isSingleModalOpen || !activeNode) return;

    const fetchAnomalyData = async () => {
      setLoading(true);
      setError(null);

      let meter_id = activeNode.data?.name?.trim() || "";
      if (/^Well_\d+$/.test(meter_id)) {
        meter_id = meter_id.replace(/^Well_(\d+)$/, "Well #$1");
      }

      const user = JSON.parse(localStorage.getItem("MCData") || "{}");

      try {
        // Fetch Anomaly Data using current modal dates
        const response = await get("apis/anoamaly_detection", {
          params: {
            meter_id: meter_id,
            start_date: modalStartDate,
            end_date: modalEndDate,
            time_range: "Custom",
          },
        });

        const payload = response.data;
        const rootObject = Array.isArray(payload) ? payload[0] : payload || {};
        const apiHardwareDetails = rootObject.meter_details?.[0] || {};

        // Fetch Schematic metadata
        const detailsRes = await get(
          `apis/schematic_meter?meter_id=${encodeURIComponent(meter_id)}&user_id=${user?.userid}`,
        );

        setSingleMeterData({
          ...detailsRes.data.data,
          ...apiHardwareDetails,
          anomalyData: (rootObject.data || []).map((item) => ({
            name: item.day,
            gallons: item.total_daily_flow,
            threshold: item.threshold_value,
            rolling: item.upper_rolling_threshold,
            historical: item.historical_guardrail_upper,
            isAnomaly: item.anomaly === 1,
            anomalyReason: item.anomaly_reason,
            suppressedReason: item.anomaly_suppressed_reason,
          })),
          total_sum: rootObject.total_flow || 0,
        });

        setIsAlertEnabled(
          detailsRes.data.data?.meter_notification_active
            ?.notification_status || false,
        );
      } catch (e) {
        console.error("Error fetching data:", e);
        setError("Failed to load meter data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAnomalyData();
  }, [activeNode, modalStartDate, modalEndDate, isSingleModalOpen]);
  const AnomalyTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const formatVal = (val) =>
        val ? `${Math.round(val).toLocaleString()} Gal` : "N/A";

      return (
        <div
          className="p-3 border rounded-lg shadow-lg min-w-[240px]"
          style={{
            backgroundColor: theme === "white" ? "#ffffff" : colors.background,
            borderColor: `${colors.primary}30`,
            backdropFilter: "blur(6px)",
          }}
        >
          <p
            className="font-bold mb-2 text-sm border-b pb-1"
            style={{ color: colors.text }}
          >
            {label}
          </p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span style={{ color: colors.textSecondary }}>Daily Flow:</span>
              <span style={{ color: colors.primary, fontWeight: "bold" }}>
                {formatVal(data.gallons)}
              </span>
            </div>
          </div>

          {/* SUPPRESSION SECTION */}
          {data.suppressedReason && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <p className="text-[11px] italic" style={{ color: "#94a3b8" }}>
                <span className="font-bold text-gray-400">Suppressed:</span>{" "}
                {data.suppressedReason}
              </p>
              {/* Show the original anomaly reason here too */}
              {data.anomalyReason && (
                <p
                  className="text-[10px] mt-1"
                  style={{ color: "#f87171", opacity: 0.8 }}
                >
                  <span className="font-semibold">Flagged for:</span>{" "}
                  {data.anomalyReason}
                </p>
              )}
            </div>
          )}

          {/* ACTIVE ANOMALY SECTION (if not suppressed) */}
          {data.isAnomaly && !data.suppressedReason && (
            <div className="mt-2 pt-2 border-t border-red-900/30">
              <div className="flex items-center gap-1.5 mb-1">
                <AlertTriangle size={12} style={{ color: "#ef4444" }} />
                <span className="text-[10px] font-bold uppercase text-red-500">
                  Anomaly Detected
                </span>
              </div>
              <p className="text-[11px]" style={{ color: "#f87171" }}>
                <span className="font-bold">Reason:</span> {data.anomalyReason}
              </p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  const RenderCustomLegend = () => {
    // Dynamically choose color based on theme
    const lineColor = theme === "white" ? colors.text : "#FFFFFF";

    const uniqueItems = [
      { label: "Flow", dash: "none", type: "flow" },
      { label: "Threshold Value", dash: "5, 5", type: "threshold_val" },
      { label: "Upper Rolling Threshold", dash: "1, 4", type: "roll_thresh" },
      {
        label: "Historical Guardrail",
        dash: "10, 3, 2, 3",
        type: "hist_guard",
      },
    ];

    return (
      <div className="flex flex-wrap justify-center gap-3 mb-6">
        {uniqueItems.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <svg width="32" height="12" className="inline-block">
              <line
                x1="0"
                y1="6"
                x2="32"
                y2="6"
                stroke={item.type === "flow" ? colors.primary : lineColor}
                strokeWidth="2.5"
                strokeDasharray={item.dash}
                strokeLinecap={item.type === "roll_thresh" ? "round" : "butt"}
              />
            </svg>
            <span
              className="text-xs font-semibold"
              style={{ color: lineColor }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const handleMultiMeterClick = useCallback(
    async (node) => {
      setLoading(true);
      setIsMultiModalOpen(true);
      setError(null);
      setMultiMeterData(null);
      setModalTitle(node.data.label);
      setMultiModalNodeType(node.data.type);

      try {
        const user = JSON.parse(localStorage.getItem("MCData") || "{}");
        const userid = user?.userid;
        let url = "";

        // FB151 SPECIAL PARAMETER LOGIC
        if (isFB151) {
          let meter_id = node.data.name;
          // Convert Well_1 style to Well #1 for the API
          if (meter_id && meter_id.includes("Well_")) {
            meter_id = meter_id.replace("Well_", "Well #");
          }
          // Use meter_id and user_id instead of total_flow
          url = `apis/schematic_meter?meter_id=${encodeURIComponent(meter_id)}&user_id=${userid}`;
        } else {
          // ORIGINAL LOGIC FOR OTHER DISTRICTS
          url = `apis/schematic_meter?total_flow=${node.data.name}`;
        }

        const response = await get(url);
        const resData = response?.data?.data;
        if (!resData) throw new Error("Invalid API response structure");

        setMultiMeterData({
          totalUsage: resData.total_sum || 0,
          unit: resData?.unit,
          flowData:
            resData?.meter_flow?.map((x) => ({
              name: x.option,
              Flow: x.flow,
            })) || [],
          meters: resData?.data_meters || [],
        });
      } catch (e) {
        console.error("Error fetching multi-meter data:", e);
        setError("Failed to load flow data. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [isFB151],
  ); // Ensure isFB151 is in the dependency array

  const onNodeClick = useCallback(
    (_, node) => {
      // 1. Skip click for the generic 'tank' structure in some districts
      if (node.data.type === "storage" && node.id === "tank") return;

      const nodeName = node.data.name;

      // 2. FB151 SPECIAL LOGIC: Every node uses the Multi-Meter API/Modal
      if (isFB151) {
        handleMultiMeterClick(node);
        return;
      }

      // 3. ORIGINAL LOGIC FOR OTHER DISTRICTS
      if (multiApiNodeNames.includes(nodeName)) {
        handleMultiMeterClick(node);
      } else if (node.data.type === "meter" || node.data.type === "source") {
        handleSingleMeterClick(node);
      } else if (
        ["storage", "consumer", "process", "supply"].includes(node.data.type)
      ) {
        handleMultiMeterClick(node);
      }
    },
    [isFB151, handleSingleMeterClick, handleMultiMeterClick, multiApiNodeNames],
  );

  const handleSingleModalCancel = () => setIsSingleModalOpen(false);
  const handleMultiModalCancel = () => setIsMultiModalOpen(false);

  const handleAlertChange = (e) => {
    const isChecked = e.target.checked;
    setIsAlertEnabled(isChecked);
    const user = JSON.parse(localStorage.getItem("MCData") || "{}");
    const meter_id = e.target.id;
    post(
      `apis/notification_meters_update?user_id=${user?.userid}&meter_id=${meter_id}&account_name=${singleMeterData?.overided_name}&status=${isChecked}&register_number=${regNo}`,
    );
  };

  const StatusBadge = ({ status, impact }) => {
    if (!status || status.length === 0) {
      return (
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
          style={{
            backgroundColor: `rgba(16, 185, 129, 0.2)`,
            color: "#10b981",
          }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
          Good
        </span>
      );
    }
    return status.map((item, index) => {
      let bgColor = "rgba(239, 68, 68, 0.2)";
      let textColor = "#ef4444";
      if (item === "Status is good") {
        bgColor = "rgba(16, 185, 129, 0.2)";
        textColor = "#10b981";
      } else if (impact <= 0.2) {
        bgColor = "rgba(234, 179, 8, 0.2)";
        textColor = "#eab308";
      } else if (impact <= 0.4) {
        bgColor = "rgba(249, 115, 22, 0.2)";
        textColor = "#f97316";
      }
      return (
        <span
          key={index}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mb-1 mr-1"
          style={{ backgroundColor: bgColor, color: textColor }}
        >
          <div
            className="w-2 h-2 p-1 rounded-full"
            style={{ backgroundColor: textColor }}
          />
          {item}
        </span>
      );
    });
  };

  // Sample meters data
  const allMetersData = [
    {
      active: true,
      operational: "warning",
      account: "Generation Park",
      meterId: "175652",
      register: "",
      dateRange: "Jun 2023 - Dec 2025",
      meterType: "Non-Potable Irrigation",
      model: "x8 Dials",
      size: "2.0",
      dials: "9",
    },
    {
      active: true,
      operational: "warning",
      account: "Generation Park",
      meterId: "47179",
      register: "",
      dateRange: "Aug 2018 - Nov 2025",
      meterType: "Non-Potable Irrigation",
      model: "x8 Dials",
      size: "2.0",
      dials: "9",
    },
    {
      active: true,
      operational: "operational",
      account: "West Lake Village",
      meterId: "47155",
      register: "",
      dateRange: "Nov 2022 - Dec 2025",
      meterType: "Potable Domestic",
      model: "x8 Dials",
      size: "2.0",
      dials: "9",
    },
    {
      active: true,
      operational: "critical",
      account: "1ST SUGARLAND INVESTMENTS",
      meterId: "47133",
      register: "",
      dateRange: "Jul 2019 - Jul 2019",
      meterType: "Potable Domestic",
      model: "x5 Dials",
      size: "2.0",
      dials: "9.0",
    },
    {
      active: true,
      operational: "critical",
      account: "1ST SUGARLAND INVESTMENTS",
      meterId: "70238033",
      register: "",
      dateRange: "Jul 2019 - Jul 2019",
      meterType: "Uncategorized",
      model: "HPT",
      size: "2.0",
      dials: "9.0",
    },
  ];

  const filteredData = allMetersData.filter((meter) => {
    if (statusFilter !== "all" && meter.operational !== statusFilter)
      return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        meter.account.toLowerCase().includes(query) ||
        meter.meterId.toLowerCase().includes(query) ||
        (meter.meterType && meter.meterType.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortColumn) return 0;
    const aValue = a[sortColumn] || "";
    const bValue = b[sortColumn] || "";
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentData = sortedData.slice(startIndex, endIndex);

  const statusCounts = {
    total: allMetersData.length,
    operational: allMetersData.filter((m) => m.operational === "operational")
      .length,
    warning: allMetersData.filter((m) => m.operational === "warning").length,
    critical: allMetersData.filter((m) => m.operational === "critical").length,
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  return (
    <>
      {/* Header */}

      <div
        className="relative w-full h-[600px] sm:h-[700px] md:h-[1050px] min-[1921px]:h-[1050px] rounded-lg border overflow-hidden mb-8"
        style={{
          // background:
          //   theme === "white" ? "#ccb9bc" : colors.cardBg || colors.background,
          color: colors.text,
          borderColor: colors.border,
        }}
      >
        <ReactFlowProvider>
          <ReactFlow
            key={districtId}
            nodes={layoutedNodes}
            edges={layoutedEdges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={fitViewOptionsValue}
            minZoom={minZoomValue}
            maxZoom={1.5}
            onNodeClick={onNodeClick}
            proOptions={{ hideAttribution: true }}
          >
            {/* Change background dots to match the dark theme */}
            <Background gap={25} size={1} color="rgba(255, 255, 255, 0.0)" />
            <Controls
              showZoom={true}
              showInteractive={false}
              style={{
                zIndex: 1000,
                top: 10,
                left: 10,
                bottom: 1000,
                pointerEvents: "none",
                color: "#000",
              }}
              className="custom-controls"
            />
          </ReactFlow>
        </ReactFlowProvider>
      </div>

      {/* Single Meter Modal */}
      <Modal
        width={1200}
        styles={{
          content: {
            backgroundColor: colors.background,
            padding: 0,
            border: `1px solid ${colors.border}`,
            maxHeight: "90vh", // Allow it to take up most of the screen height
          },
          body: {
            padding: 0,
            height: "100%", // Ensure body fills the content
          },
        }}
        open={isSingleModalOpen}
        onCancel={handleSingleModalCancel}
        footer={null}
        centered
        closeIcon={false}
        className="dark-theme-modal"
      >
        {loading && (
          <div className="text-center p-12">
             <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg">
                  <Spin size="large" />
              </div>
          </div>
        )}
        {error && (
          <div className="text-center p-12 text-red-500 font-semibold">
            {error}
          </div>
        )}
        {!loading && !error && singleMeterData && (
          <div>
            <div
              className="sticky top-0 z-10 flex items-center justify-between p-6 border-b"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg border flex items-center justify-center"
                  style={{
                    backgroundImage: colors.gradient,
                    borderColor: colors.primary,
                  }}
                >
                  <Gauge className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2
                    className="text-2xl font-bold"
                    style={{ color: colors.text }}
                  >
                    Meter Usage
                  </h2>
                  {/* CONDITIONALLY RENDERED METER ID */}
                  {!isFB151 && (
                    <p
                      onClick={() => goToMeterLeakDetails(singleMeterData)}
                      className="text-sm cursor-pointer"
                      style={{ color: colors.textSecondary }}
                    >
                      Meter ID: {singleMeterData.meter_id}
                    </p>
                  )}
                </div>
              </div>
              {/* DATEPICKER INTEGRATION */}
             
              <button
                onClick={handleSingleModalCancel}
                className="w-10 h-10 rounded-lg border flex items-center justify-center transition-all hover:bg-opacity-20"
                style={{
                  borderColor: colors.border,
                  backgroundColor: "transparent",
                  color: colors.textSecondary,
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <div className="grid grid-cols-12 gap-4">
                  {/* 🔵 LEFT SIDE - GRAPH */}
                  <div
                    className="col-span-12 lg:col-span-8 border rounded-lg p-4"
                    style={{
                      borderColor: colors.border,
                      backgroundColor: colors.cardBg,
                    }}
                  >
                     <div className="flex items-center gap-3 justify-end mb-5">
                <span
                  className="text-xs font-bold"
                  style={{ color: colors.textSecondary }}
                >
                  Range:
                </span>
                <ConfigProvider
                  theme={{
                    algorithm:
                      theme === "white"
                        ? antTheme.defaultAlgorithm
                        : antTheme.darkAlgorithm,
                  }}
                >
                  <RangePicker
                    value={[dayjs(modalStartDate), dayjs(modalEndDate)]}
                    onChange={(dates) => {
                      if (dates) {
                        setModalStartDate(dates[0].format("YYYY-MM-DD"));
                        setModalEndDate(dates[1].format("YYYY-MM-DD"));
                      }
                    }}
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                    style={{
                      backgroundColor: colors.background,
                      borderColor: colors.border,
                      color: colors.text,
                    }}
                  />
                </ConfigProvider>
              </div>
                    <div className="h-[455px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={singleMeterData?.anomalyData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={colors.border}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="name"
                            stroke={colors.textSecondary}
                            fontSize={10}
                          />
                          <YAxis stroke={colors.textSecondary} fontSize={10} />
                          <Tooltip content={<AnomalyTooltip />} />

                          {/* CALL CUSTOM LEGEND HERE */}
                          <Legend
                            content={<RenderCustomLegend />}
                            verticalAlign="top"
                            height={50}
                          />

                          <Line
                            type="monotone"
                            dataKey="threshold"
                            stroke={colors.textSecondary}
                            strokeDasharray="5 5"
                            dot={false}
                            connectNulls
                            opacity={0.4}
                          />
                          <Line
                            type="monotone"
                            dataKey="rolling"
                            stroke={colors.textSecondary}
                            strokeDasharray="1 4"
                            dot={false}
                            connectNulls
                            opacity={0.4}
                          />
                          <Line
                            type="monotone"
                            dataKey="historical"
                            stroke={colors.textSecondary}
                            strokeDasharray="10 3 2 3"
                            dot={false}
                            connectNulls
                            opacity={0.4}
                          />
                          <Line
                            type="monotone"
                            dataKey="gallons"
                            stroke={colors.primary}
                            strokeWidth={3}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* 🟢 RIGHT SIDE - INFO PANEL */}
                  <div
                    className="col-span-12 lg:col-span-4 space-y-4 border rounded-lg p-4 h-[530px] overflow-y-scroll"
                    style={{
                      borderColor: colors.border,
                      backgroundColor: colors.cardBg,
                      height: "530px",
                    }}
                  >
                    <h4
                      style={{ color: colors.textSecondary }}
                      className="mb-2 text-sm"
                    >
                      Meter Information
                    </h4>

                    <div
                      className="flex justify-between items-start flex-col bg-opacity-10 p-4 rounded-lg border gap-3"
                      style={{
                        backgroundColor: `${colors.primary}10`,
                        borderColor: colors.border,
                      }}
                    >
                      {/* CONDITIONALLY RENDERED ADD TO ALERT */}
                      {!isFB151 && (
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={singleMeterData.meter_id}
                            onChange={handleAlertChange}
                            checked={isAlertEnabled}
                            className="w-4 h-4 rounded focus:ring-2 cursor-pointer"
                            style={{ accentColor: colors.primary }}
                          />
                          <label
                            htmlFor={singleMeterData.meter_id}
                            className="text-sm font-medium cursor-pointer"
                            style={{ color: colors.textSecondary }}
                          >
                            Add to alert
                          </label>
                        </div>
                      )}
                    </div>

                    <div
                      className=""
                      style={{
                        backgroundColor: colors.cardBg,
                        borderColor: colors.border,
                      }}
                    >
                      <div className="text-sm space-y-2">
                        <div className="grid grid-cols-1 gap-3">
                          <InfoTile
                            label="Meter ID"
                            value={singleMeterData.meter_id}
                            icon={<Hash size={18} />}
                          />
                          <InfoTile
                            label="Account / Overided Name"
                            value={singleMeterData.overided_name}
                            icon={<Building2 size={18} />}
                          />
                        </div>

                        {/* 2. FLOW SUMMARY (Highlighted Style) */}
                        <div
                          className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                          style={{
                            backgroundColor: `${colors.primary}10`,
                            borderColor: `${colors.primary}40`,
                          }}
                        >
                          <div className="mt-1 text-cyan-400">
                            <Droplet size={18} />
                          </div>
                          <div className="flex-1">
                            <span
                              className="text-xs font-semibold block mb-1"
                              style={{ color: colors.textSecondary }}
                            >
                              Total Water Flow (Selected Range)
                            </span>
                            <p
                              className="text-lg font-bold"
                              style={{ color: "#ffffff" }}
                            >
                              {singleMeterData.total_sum?.toLocaleString(
                                "en-US",
                              )}{" "}
                              Gallons
                            </p>
                          </div>
                        </div>

                        {/* 3. TECHNICAL HARDWARE DETAILS */}
                        <div className="grid grid-cols-1 gap-3">
                          <InfoTile
                            label="Meter Serial Number"
                            value={singleMeterData.meter_sn}
                            icon={<Gauge size={18} />}
                          />
                          <InfoTile
                            label="Endpoint Serial Number"
                            value={singleMeterData.endpoint_sn}
                            icon={<Activity size={18} />}
                          />
                        </div>

                        {/* 4. STATUS & CONNECTIVITY */}
                        <div className="grid grid-cols-2 gap-3">
                          <div
                            className="backdrop-blur-sm rounded-xl border p-4"
                            style={{
                              backgroundColor: colors.cardBg,
                              borderColor: colors.border,
                            }}
                          >
                            <div className="flex items-center gap-2 mb-1 text-green-400">
                              <Battery size={16} />
                              <span className="text-[10px] font-bold uppercase text-slate-400">
                                Battery
                              </span>
                            </div>
                            <p
                              className="text-sm font-bold capitalize"
                              style={{ color: colors.text }}
                            >
                              {singleMeterData.battery_level}
                            </p>
                          </div>
                          <div
                            className="backdrop-blur-sm rounded-xl border p-4"
                            style={{
                              backgroundColor: colors.cardBg,
                              borderColor: colors.border,
                            }}
                          >
                            <div className="flex items-center gap-2 mb-1 text-cyan-400">
                              <Wifi size={16} />
                              <span className="text-[10px] font-bold uppercase text-slate-400">
                                Signal
                              </span>
                            </div>
                            <p
                              className="text-sm font-bold capitalize"
                              style={{ color: colors.text }}
                            >
                              {singleMeterData.signal_strength}
                            </p>
                          </div>
                        </div>

                        <div
                          className="p-4 rounded-xl border"
                          style={{
                            backgroundColor: colors.cardBg,
                            borderColor: colors.border,
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2 text-cyan-400">
                            <MapPin size={18} />
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                              Service Address
                            </h3>
                          </div>
                          <p
                            className="text-sm leading-relaxed"
                            style={{ color: colors.text }}
                          >
                            {singleMeterData.service_address}
                          </p>
                        </div>

                        {/* 6. SYSTEM LIFECYCLE & TIMESTAMPS */}
                        <div
                          className="p-4 rounded-xl border space-y-4"
                          style={{
                            backgroundColor: `${colors.cardBg}80`,
                            borderColor: colors.border,
                          }}
                        >
                          <div className="flex items-center gap-2 text-cyan-400">
                            <Clock size={18} />
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                              System Logs
                            </h3>
                          </div>

                          <div className="grid grid-cols-1 gap-4">
                            <div className="border-l-2 border-gray-700 pl-3">
                              <span className="text-[10px] font-semibold block uppercase text-slate-500">
                                Latest Flow Recorded
                              </span>
                              <p
                                className="text-xs font-medium mt-1"
                                style={{ color: colors.text }}
                              >
                                {singleMeterData.latest_flow_time}
                              </p>
                            </div>
                            <div className="border-l-2 border-gray-700 pl-3">
                              <span className="text-[10px] font-semibold block uppercase text-slate-500">
                                Last Communication
                              </span>
                              <p
                                className="text-xs font-medium mt-1"
                                style={{ color: colors.text }}
                              >
                                {singleMeterData.last_comm_time}
                              </p>
                            </div>
                            <div className="border-l-2 border-gray-700 pl-3">
                              <span className="text-[10px] font-semibold block uppercase text-slate-500">
                                Installation Date
                              </span>
                              <p
                                className="text-xs font-medium mt-1"
                                style={{ color: colors.text }}
                              >
                                {singleMeterData.meter_install_date}
                              </p>
                            </div>
                            <div className="border-l-2 border-gray-700 pl-3">
                              <span className="text-[10px] font-semibold block uppercase text-slate-500">
                                Endpoint Status
                              </span>
                              <p className="text-xs font-bold mt-1 text-green-400">
                                {singleMeterData.m_endpoint_status}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Multi Meter Modal */}
      <Modal
        width={1000}
        open={isMultiModalOpen}
        onCancel={handleMultiModalCancel}
        footer={null}
        centered
        closeIcon={false}
        className="dark-theme-modal"
        styles={{
          content: {
            backgroundColor: colors.background,
            padding: 0,
            border: `1px solid ${colors.border}`,
          },
          body: { padding: 0 },
        }}
      >
        {loading && (
          <div className="text-center p-12">
             <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg">
             <Spin size="large" />
          </div>
          </div>
        )}
        {error && (
          <div className="text-center p-12 text-red-500 font-semibold">
            {error}
          </div>
        )}
        {!loading && !error && multiMeterData && (
          <div>
            <div
              className="sticky top-0 z-10 flex items-center justify-between p-6 border-b"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg border flex items-center justify-center"
                  style={{
                    backgroundImage: colors.gradient,
                    borderColor: colors.primary,
                  }}
                >
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2
                    className="text-2xl font-bold"
                    style={{ color: colors.text }}
                  >
                    {multiModalNodeType === "meter"
                      ? "Total Flow"
                      : multiModalNodeType === "storage" && isFB151
                        ? "Tank Level"
                        : modalTitle}
                  </h2>
                  <p
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {/* [Last 30 Daysss] */}
                  </p>
                </div>
              </div>
              <button
                onClick={handleMultiModalCancel}
                className="w-10 h-10 rounded-lg border flex items-center justify-center transition-all hover:bg-opacity-20"
                style={{
                  borderColor: colors.border,
                  backgroundColor: "transparent",
                  color: colors.textSecondary,
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* </div> */}
              <div
                // className="border rounded-lg p-4 relative max-[800px]:overflow-x-scroll"
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr", // LEFT BIG, RIGHT SMALL
                  gap: "16px",
                }}
              >
                <div
                  className=" min-[1921px]:h-[500px] max-[800px]:w-[600px]"
                  style={{
                    height: "475px",
                    borderRadius: "10px",
                    border: `1px solid ${colors.border}`,
                    padding: "10px",
                    backgroundColor: colors.cardBg,
                  }}
                >
                  {/* Added margin to ensure labels are visible */}
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={multiMeterData.flowData}
                      margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
                    >
                      <defs>
                        <linearGradient
                          id="multiGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor={colors.primary}
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="100%"
                            stopColor={colors.primary}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={colors.border}
                      />
                      <XAxis
                        dataKey="name"
                        stroke={colors.textSecondary}
                        style={{ fontSize: "12px" }}
                        label={{
                          value:
                            isFB151 && multiModalNodeType === "storage"
                              ? "Level"
                              : "Flow",
                          position: "insideBottom",
                          offset: -10,
                          fill: "#ffffff",
                          style: { textAnchor: "middle" },
                        }}
                      />
                      <YAxis
                        stroke={colors.textSecondary}
                        style={{ fontSize: "12px" }}
                        width={80}
                        tickFormatter={(v) => v.toLocaleString("en-US")}
                        label={{
                          value:
                            isFB151 && multiModalNodeType === "storage"
                              ? "Feet"
                              : "Gallons",
                          angle: -90,
                          position: "insideLeft",
                          offset: 0,
                          fill: "#ffffff",
                          style: { textAnchor: "middle" },
                        }}
                      />
                      <Tooltip
                        formatter={(value) => [
                          value.toLocaleString("en-US"),
                          "Level",
                        ]}
                        contentStyle={{
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          borderRadius: "8px",
                        }}
                        labelStyle={{ color: colors.text }}
                      />
                      <Line
                        type="monotone"
                        dataKey="Flow"
                        name={
                          isFB151 && multiModalNodeType === "storage"
                            ? "Level"
                            : "Flow"
                        }
                        stroke={colors.primary}
                        strokeWidth={2}
                        fill="url(#multiGradient)"
                        dot={{ fill: colors.primary, r: 4 }}
                      />
                    </LineChart>
                    {/* Replace the LineChart inside Single Meter Modal with this */}
                  </ResponsiveContainer>
                </div>
                <div
                  className="space-y-4 border rounded-lg p-4 h-[475px] overflow-y-scroll"
                  style={{
                    borderColor: colors.border,
                    backgroundColor: colors.cardBg,
                    height: "475px",
                  }}
                >
                  <h4
                    style={{ color: colors.textSecondary }}
                    className="mb-2 text-sm"
                  >
                    Meter Information
                  </h4>

                  {!(isFB151 && multiModalNodeType === "storage") && (
                    <div
                      className="flex justify-between items-center bg-opacity-10 p-4 rounded-lg border mb-3"
                      style={{
                        backgroundColor: `${colors.primary}10`,
                        borderColor: colors.border,
                      }}
                    >
                      <div>
                        <h5
                          className="font-semibold text-lg"
                          style={{ color: colors.text }}
                        >
                          {multiMeterData.totalUsage?.toLocaleString("en-US")}{" "}
                          {multiMeterData.unit}
                        </h5>
                        <p
                          className="text-xs mb-0"
                          style={{ color: colors.textSecondary }}
                        >
                          [Last 30 Days]
                        </p>
                      </div>
                    </div>
                  )}
                  {["supply", "consumer", "process"].includes(
                    multiModalNodeType,
                  ) && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        overflowY: "auto",
                      }}
                    >
                      {multiMeterData.meters.map((meter, index) => (
                        <div
                          key={index}
                          style={{
                            padding: "12px",
                            borderRadius: "10px",
                            border: `1px solid ${colors.border}`,
                            backgroundColor: colors.cardBg,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "12px",
                              color: colors.textSecondary,
                            }}
                            className="flex items-center gap-1 mb-1"
                          >
                            <Hash size={18} />
                            Meter ID
                          </div>

                          <div
                            style={{ color: colors.text, fontWeight: "600" }}
                            className="ms-5"
                          >
                            {meter.meter_id}
                          </div>
                           {/* <InfoTile
                            label="Meter ID"
                            value={meter.meter_id}
                            icon={<Hash size={18} />}
                          /> */}

                          <div style={{ marginTop: "6px" }}>
                            <StatusBadge
                              status={meter.status}
                              impact={meter.impact}
                            />
                          </div>

                          <div
                            style={{
                              marginTop: "6px",
                              fontSize: "12px",
                              color: colors.text,
                            }}
                          >
                            {meter.overided_name}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
